import { useContext, useState } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import MeterAttributeCard from '@atom/components/inventoryDetail/MeterAttributeCard';
import { Icon, IconButton, Modal } from '@atom/mui';
import colors from '@atom/styles/colors';
import {
  AttributesType,
  HydratedAttributeGroupsItem,
} from '@atom/types/inventory';
import attributeDisplayUtility from '@atom/utilities/attributeDisplayUtility';

import AttributeDetailsContext, {
  AttributeView,
} from './AttributeDetailsContext';

import classes from './attributeDetails.module.css';

const styles = {
  icon: {
    padding: '0px',
    marginLeft: '0.5rem',
    height: '35px',
    width: '35px',
    bottom: '10px',
  },
};

interface Props {
  attribute: AttributesType;
  attributeGroup: HydratedAttributeGroupsItem;
}

const AttributeRow = ({ attribute, attributeGroup }: Props) => {
  const { onPendingApproval, canManageChanges, errorAttributes, view, asset } =
    useContext(AttributeDetailsContext);

  // PM extension: meter-flagged attributes render the live meter UI inline
  // (sparkline, expandable history, update-reading action) in place of the
  // plain read-only value. Keeps the row aligned with sibling attributes.
  if (
    attribute.isMeter &&
    view === AttributeView.ASSET_DETAIL &&
    asset?.id
  ) {
    return (
      <MeterAttributeCard
        assetId={asset.id}
        attributeId={attribute.id}
        attributeName={attribute.name}
        unit={attribute.unit}
      />
    );
  }

  const [open, setOpen] = useState<boolean>(false);

  const disableControls = !!attribute.changeId && !attribute.isEditable;

  const onRejectClick = () => {
    if (!disableControls) {
      setOpen(true);
    }
  };

  const onApproveCLick = () => {
    if (!disableControls) {
      onPendingApproval('approve', attributeGroup.name, attribute);
    }
  };

  const onRejectConfirm = () => {
    onPendingApproval('reject', attributeGroup.name, attribute);
    setOpen(false);
  };

  const hasAttributeError = R.includes(
    attribute?.id,
    R.pathOr([], [attributeGroup.id], errorAttributes),
  );
  const labelStyle = () => {
    return clsx(
      classes.attributeLabel,
      view === AttributeView.MAP_TRAY && classes.tray,
      hasAttributeError && classes.error,
    );
  };
  const showControls = !!attribute.changeId && canManageChanges;
  const unitSuffix = ` (${attribute.unit})`;
  const controlButtonsColor = disableControls
    ? colors.neutral.gray
    : colors.brand.blue;
  const disabledTooltip =
    'The pending change cannot be approved or rejected because the attribute is not editable. Please set the attribute to editable in the asset settings to approve or reject changes.';
  const rejectTooltip = disableControls ? disabledTooltip : 'Reject';
  const approveTooltip = disableControls ? disabledTooltip : 'Approve';
  const attributeName = attribute.isRequired
    ? `* ${attribute.name}`
    : attribute.name;
  const value =
    view === AttributeView.SCHEMA_PREVIEW
      ? attribute?.defaultValue
      : attribute?.value;

  const approvalContainer = clsx(
    classes.attributeApprovalContainer,
    view === AttributeView.MAP_TRAY && classes.tray,
  );

  const valueContent = (
    <>
      <div className={classes.attributeValue}>
        {attributeDisplayUtility.display(
          value,
          attribute.dataType,
          attribute.unit,
          !!attribute.changeId,
          attribute.newValue,
          attribute.thousandsSeparator,
        )}
      </div>
      {showControls && (
        <div className={approvalContainer}>
          <div className={classes.pendingAttributeLabel}>Edited</div>
          <div className={classes.pendingAttributeControl}>
            <IconButton
              style={styles.icon}
              tooltip={rejectTooltip}
              onClick={onRejectClick}
            >
              <Icon color={controlButtonsColor}>clear</Icon>
            </IconButton>
          </div>
          <div className={classes.pendingAttributeControl}>
            <IconButton
              tooltip={approveTooltip}
              style={styles.icon}
              onClick={onApproveCLick}
            >
              <Icon color={controlButtonsColor}>check</Icon>
            </IconButton>
          </div>
        </div>
      )}
    </>
  );

  return (
    <>
      <div className={classes.attributeListRow}>
        <div className={labelStyle()}>
          {attributeName}
          {attribute.unit && !attribute.name.endsWith(unitSuffix) && unitSuffix}
        </div>
        {view === AttributeView.MAP_TRAY ? (
          <div className={classes.trayContentContainer}>{valueContent}</div>
        ) : (
          valueContent
        )}
      </div>
      <Modal
        open={open}
        cancelButtonText="Cancel"
        confirmButtonText="Reject"
        onCancel={() => setOpen(false)}
        onConfirm={onRejectConfirm}
        title="Reject Changes"
      >
        Are you sure you want to reject the changes on this attribute?
      </Modal>
    </>
  );
};

export default AttributeRow;
